import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { User, Calendar, Briefcase, Upload, Eye, EyeOff, Check, X } from "lucide-react"
import { useState, useEffect } from "react"
import { useAdminAuth } from "@/context/Admin"
import Loader from "@/components/loader/loader"
import { getPasswordStrength, getStrengthText, getStrengthColor } from "@/lib/password-utils"
import { toast } from "sonner"
import ActiveSessions from "./active-sessions"

export default function AdminProfile() {
  const { admin, fetchAdmin, getAdminLoginHistory, uploadAdminAvatar, verifyPassword, changePassword } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  // Password change states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")
  const [isUpdating, setIsUpdating] = useState(false)
  const [passwordVerified, setPasswordVerified] = useState(false)

  const passwordStrength = getPasswordStrength(newPassword)
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (!admin) {
        await fetchAdmin();
      }
      setLoading(false);
    };
    loadData();
  }, [admin, fetchAdmin]);

  useEffect(() => {
    const loadLoginHistory = async () => {
      setLoadingHistory(true);
      try {
        const data = await getAdminLoginHistory(10, 0);
        setLoginHistory(data.history || []);
      } catch (error) {
        console.error("Error loading login history:", error);
      } finally {
        setLoadingHistory(false);
      }
    };
    if (admin) {
      loadLoginHistory();
    }
  }, [admin, getAdminLoginHistory]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('File size exceeds 5MB limit. Please choose a smaller image.');
      return;
    }

    setUploadingAvatar(true);
    try {
      await uploadAdminAvatar(file);
      // Admin state is automatically updated in the context
      await fetchAdmin(); // Refresh admin data to get updated avatar URL
    } catch (error) {
      console.error("Error uploading avatar:", error);
      // Error toast is handled in the context
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      e.target.value = '';
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (success) => {
    return success ? (
      <Badge className="bg-green-100 text-green-700">Success</Badge>
    ) : (
      <Badge className="bg-red-100 text-red-700">Failed</Badge>
    );
  };

  const handleVerifyPassword = async () => {
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }

    try {
      const isValid = await verifyPassword(currentPassword);
      if (isValid) {
        setPasswordVerified(true);
        toast.success("Password verified successfully");
      }
    } catch (error) {
      setPasswordVerified(false);
      // Error toast is shown by verifyPassword function
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordVerified) {
      toast.error("Please verify your current password first");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (getPasswordStrength(newPassword) < 75) {
      toast.error("Password is too weak. Please use a stronger password.");
      return;
    }

    setIsUpdating(true);
    try {
      await changePassword(currentPassword, newPassword);
      
      // Reset form on success
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setPasswordVerified(false);
    } catch (error) {
      console.error("Error updating password:", error);
      // Show detailed validation errors if available
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach(err => {
          toast.error(err);
        });
      }
      // Error toast is handled in the context
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const adminName = admin?.name || "Admin User";
  const adminEmail = admin?.email || "admin@example.com";
  const adminRole = admin?.role || "admin";
  const createdAt = admin?.created_at || null;

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <User className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold text-primary">Admin Profile</h1>
      </div>
      <p className="text-gray-600">Manage your personal information and account details.</p>

      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Personal Information</CardTitle>
          <CardDescription>Update your name, email, and profile picture.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar Section */}
          <div className="flex items-center gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage 
                src={admin?.avatar_url || "/placeholder.svg"} 
                alt="Admin Avatar"
                onError={(e) => {
                  e.target.src = "/placeholder.svg";
                }}
              />
              <AvatarFallback className="bg-secondary text-primary text-4xl">
                {adminName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <Label htmlFor="avatar-upload" className="cursor-pointer">
                <Button asChild disabled={uploadingAvatar}>
                  <span className="flex items-center gap-2">
                    <Upload className="w-4 h-4" /> 
                    {uploadingAvatar ? "Uploading..." : "Upload New Avatar"}
                  </span>
                </Button>
              </Label>
              <Input 
                id="avatar-upload" 
                type="file" 
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden" 
                onChange={handleAvatarUpload}
                disabled={uploadingAvatar}
              />
              <p className="text-sm text-gray-500">Max file size: 5MB (JPEG, PNG, WebP)</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={adminName}
                  disabled
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={adminEmail}
                  disabled
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Input
                value={adminRole === "super_admin" ? "Super Admin" : "Admin"}
                disabled
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-primary">Account Details</CardTitle>
          <CardDescription>Information about your administrator account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-gray-700">
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-secondary" />
            <div>
              <p className="font-medium">Role:</p>
              <p className="capitalize">{adminRole === "super_admin" ? "Super Administrator" : "Administrator"}</p>
            </div>
          </div>
          <Separator />
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-secondary" />
            <div>
              <p className="font-medium">Account Created:</p>
              <p>{createdAt ? formatDate(createdAt) : "N/A"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-primary">Change Password</CardTitle>
          <CardDescription>Update your administrator password to keep your account secure.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="currentPassword">Current Password</Label>
            <div className="relative mt-1">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                placeholder="Enter current password"
                className="pr-10"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setPasswordVerified(false);
                }}
                disabled={passwordVerified}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {!passwordVerified && currentPassword && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleVerifyPassword}
                className="mt-2"
              >
                Verify Password
              </Button>
            )}
            {passwordVerified && (
              <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Password verified
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative mt-1">
              <Input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter new password"
                className="pr-10"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {newPassword && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength)}`}
                      style={{ width: `${passwordStrength}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-600">{getStrengthText(passwordStrength)}</span>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <div className="flex items-center gap-2">
                    {newPassword.length >= 8 ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>At least 8 characters (max 128)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[A-Z]/.test(newPassword) ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>One uppercase letter</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[a-z]/.test(newPassword) ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>One lowercase letter</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[0-9]/.test(newPassword) ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>One number</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>One special character (!@#$%^&*...)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {newPassword.length >= 12 ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>12+ characters recommended</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
            <div className="relative mt-1">
              <Input
                id="confirmNewPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                className="pr-10"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {confirmNewPassword && newPassword !== confirmNewPassword && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <X className="w-3 h-3" />
                Passwords do not match
              </p>
            )}
            {confirmNewPassword && newPassword === confirmNewPassword && (
              <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Passwords match
              </p>
            )}
          </div>
          <Button 
            className="bg-primary hover:bg-primary/90 text-white"
            onClick={handleUpdatePassword}
            disabled={
              isUpdating || 
              !passwordVerified || 
              newPassword !== confirmNewPassword || 
              getPasswordStrength(newPassword) < 75 ||
              newPassword.length < 8 ||
              !/[A-Z]/.test(newPassword) ||
              !/[a-z]/.test(newPassword) ||
              !/[0-9]/.test(newPassword) ||
              !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)
            }
          >
            {isUpdating ? "Updating..." : "Update Password"}
          </Button>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-primary">Recent Login History</CardTitle>
          <CardDescription>Your recent login attempts and sessions.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <Loader />
          ) : loginHistory.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loginHistory.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{formatDate(entry.created_at)}</TableCell>
                    <TableCell className="font-mono text-sm">{entry.ip_address || "N/A"}</TableCell>
                    <TableCell className="capitalize">{entry.auth_method || "Password"}</TableCell>
                    <TableCell>{getStatusBadge(entry.success === 1 || entry.success === true)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-gray-500 text-center py-4">No login history available.</p>
          )}
        </CardContent>
      </Card>

      <ActiveSessions />
    </div>
  )
}
